import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ChartCard from "./ChartCard";

/** Always return a label string so tiny bars are never skipped by MUI's truthy check. */
const formatBarLabel = (item) => {
  if (item?.value == null) {
    return null;
  }
  return String(item.value);
};

const CategoryBarChart = ({
  title,
  subtitle,
  data,
  isRTL,
  chartHeight = 380,
  color = "#1976d2",
}) => {
  const { t } = useTranslation();

  const { labels, counts, hasData } = useMemo(() => {
    const sorted = [...(data || [])].sort((a, b) => b.count - a.count);
    const withTickets = sorted.filter((item) => item.count > 0);
    return {
      labels: withTickets.map((item) =>
        isRTL ? item.name_ar : item.name_en || item.name_ar
      ),
      counts: withTickets.map((item) => item.count),
      hasData: withTickets.length > 0,
    };
  }, [data, isRTL]);

  const useRotatedLabels = labels.length > 4;

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      height={chartHeight + 80}
      isRTL={isRTL}
      exportCategories={labels}
      exportSeries={[{ name: t("reports.charts.ticketCount"), data: counts }]}
    >
      {(layoutHeight, _expanded, showLabels) =>
        !hasData ? (
          <Box
            sx={{
              height: layoutHeight,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">{t("reports.noData")}</Typography>
          </Box>
        ) : (
          <BarChart
            xAxis={[
              {
                scaleType: "band",
                data: labels,
                height: "auto",
                tickLabelStyle: {
                  fontSize: 12,
                  ...(useRotatedLabels
                    ? { angle: -35, textAnchor: "end" }
                    : { textAnchor: "middle" }),
                },
              },
            ]}
            yAxis={[
              {
                width: 60,
                // MUI inverts textAnchor in RTL, which pushes Y labels into the plot.
                // Force "end" so values stay outside (to the left of) the axis line.
                tickLabelStyle: { textAnchor: "end", fontSize: 12 },
              },
            ]}
            series={[
              {
                data: counts,
                label: t("reports.charts.ticketCount"),
                color,
                // Keep a minimum pixel height so outside labels stay above the axis
                // (height 0 makes MUI place labels below the origin → clipped).
                minBarSize: 3,
                barLabel: showLabels ? formatBarLabel : undefined,
                barLabelPlacement: "outside",
              },
            ]}
            height={layoutHeight}
            margin={{
              top: showLabels ? 36 : 24,
              bottom: 16,
              left: 8,
              right: 16,
            }}
            grid={{ horizontal: true }}
            slotProps={{
              barLabel: {
                style: {
                  fontSize: 11,
                  paintOrder: "stroke",
                  stroke: "#fff",
                  strokeWidth: 2,
                },
              },
            }}
          />
        )
      }
    </ChartCard>
  );
};

export default CategoryBarChart;
